// One quote (round 43, spec 12): its details, its prices, its notes, its
// files and its history.
//
// Round 45 (Nathan's walkthrough): Description and Exclusions are tabs of
// their own between Details and Pricing, as Zanetto Tools has them; Details
// shows every field, with a dash for one nobody filled in; the prices are
// one spreadsheet row per line; History says what people actually did; and
// a revision is made with a button that says so.
//
// The page never decides what is allowed. The server's detail response says
// which statuses this quote may move to and whether it may be edited, revised,
// converted or deleted (lib/quoteStatus.ts), so the buttons a person sees and
// the requests the server accepts are one answer.
//
// Saves carry the quote's lockVersion. When somebody else saved first, the
// server answers 409 and the page says so in plain words and offers a reload,
// rather than silently writing over them.
import { useCallback, useEffect, useMemo, useState, type KeyboardEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ApiError } from '../api';
import { useSession } from '../session';
import { notifications } from '../notificationsApi';
import { scheduling, type Client, type ClientRep } from '../schedulingApi';
import { documents, type Attachment } from '../documentsApi';
import { formatDate, formatMomentAu } from '../lib/dates';
import { useTabParam } from '../lib/useTabParam';
import { Banner, Field } from '../ui';
import { PageHeader } from '../components/ui/Page';
import { Modal } from '../components/ui/Modal';
import { ActionMenu } from '../components/ui/ActionMenu';
import { useConfirm } from '../components/ui/confirm';
import { QUOTE_STATUS_COLOURS, StatusSelect } from '../components/ui/StatusSelect';
import { NotesPanel } from '../components/NotesPanel';
import { RepModal } from '../components/clients/RepsPanel';
import { Documents } from '../components/Documents';
import {
  QUOTE_STATUS_LABEL,
  centsToInput,
  formatCents,
  quotesApi,
  type LineInput,
  type QuoteDetail,
  type QuoteLine,
  type QuoteRevision,
  type QuoteStatus,
} from '../quotesApi';

const QUOTE_TABS = [
  { key: 'details', label: 'Details' },
  { key: 'description', label: 'Description' },
  { key: 'exclusions', label: 'Exclusions' },
  { key: 'pricing', label: 'Pricing' },
  { key: 'notes', label: 'Internal notes' },
  { key: 'files', label: 'Files' },
  { key: 'history', label: 'History' },
] as const;
const QUOTE_TAB_KEYS = QUOTE_TABS.map((t) => t.key);

const STALE_MESSAGE =
  'Someone else changed this quote while you had it open. Reload to see their changes, then make yours again.';

function isStale(err: unknown): boolean {
  return (
    err instanceof ApiError &&
    err.status === 409 &&
    (err.details as { code?: string } | undefined)?.code === 'stale'
  );
}

export function QuoteDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { user } = useSession();
  const isAdmin = user?.role === 'admin';
  const [detail, setDetail] = useState<QuoteDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [stale, setStale] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [tab, setTab] = useTabParam(QUOTE_TAB_KEYS);
  const [datesOpen, setDatesOpen] = useState(false);
  const [convertOpen, setConvertOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    try {
      setDetail(await quotesApi.get(id));
      setError(null);
      setStale(false);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load that quote.');
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  /** Runs one action, puts its answer on the page, and says why if it failed. */
  const act = useCallback(
    async (action: () => Promise<QuoteDetail | void>, success?: string) => {
      setBusy(true);
      setError(null);
      setNotice(null);
      try {
        const result = await action();
        if (result) setDetail(result);
        if (success) setNotice(success);
      } catch (err) {
        if (isStale(err)) setStale(true);
        else setError(err instanceof ApiError ? err.message : 'Something went wrong. Try again.');
      } finally {
        setBusy(false);
      }
    },
    []
  );

  if (!detail) {
    return error ? <Banner kind="error">{error}</Banner> : <p className="muted">Loading…</p>;
  }

  const { quote, revision, can } = detail;
  const archived = quote.archived;

  async function changeStatus(next: QuoteStatus) {
    if (next === quote.status) return;
    if (next === 'draft' && quote.status !== 'draft') {
      const ok = await confirm({
        title: 'Set this quote back to Draft?',
        message:
          'Its details and prices unlock for changes. Use this to fix something before it goes out again; to re-price a quote the client has already seen, make a revision instead.',
        confirmLabel: 'Set to Draft',
        tone: 'warning',
      });
      if (!ok) return;
    }
    await act(() => quotesApi.setStatus(quote.id, next), `Marked ${QUOTE_STATUS_LABEL[next]}.`);
  }

  async function revise() {
    const ok = await confirm({
      title: `Create revision ${quote.currentRevision + 1} of ${quote.number}?`,
      message:
        'The quote number stays the same. The new revision starts as a draft with a copy of the prices, ready to change; the current one stays readable in History exactly as it was sent.',
      confirmLabel: 'Create revision',
      tone: 'primary',
    });
    if (!ok) return;
    await act(async () => {
      const result = await quotesApi.revise(quote.id);
      setTab('pricing');
      return result;
    }, `Revision ${quote.currentRevision + 1} started as a draft.`);
  }

  async function archiveToggle() {
    if (!archived) {
      const ok = await confirm({
        title: 'Archive this quote?',
        message: 'It leaves the lists but is kept, with its history. You can restore it from the Archived tab.',
        confirmLabel: 'Archive',
        tone: 'warning',
      });
      if (!ok) return;
    }
    await act(
      () => (archived ? quotesApi.unarchive(quote.id) : quotesApi.archive(quote.id)),
      archived ? 'Restored.' : 'Archived.'
    );
  }

  async function remove() {
    const ok = await confirm({
      title: `Delete ${quote.number} for good?`,
      message:
        'It was never sent, so it can be removed completely, with its notes and files. This cannot be undone.',
      confirmLabel: 'Delete quote',
      tone: 'danger',
      requireText: quote.number,
    });
    if (!ok) return;
    setBusy(true);
    try {
      await quotesApi.remove(quote.id);
      navigate('/app/quotes', { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not delete that quote.');
      setBusy(false);
    }
  }

  return (
    <>
      <PageHeader
        title={`${quote.number} · ${quote.title}`}
        subtitle={
          <>
            {quote.clientName}
            {quote.currentRevision > 1 && (
              <>
                {' '}
                <RevisionPill revision={quote.currentRevision} round />
              </>
            )}
            {archived && <> · archived</>}
          </>
        }
        actions={
          <>
            <StatusSelect<QuoteStatus>
              value={quote.status}
              options={[quote.status, ...detail.allowedStatuses]}
              labels={QUOTE_STATUS_LABEL}
              colours={QUOTE_STATUS_COLOURS}
              label="Status"
              disabled={busy || detail.allowedStatuses.length === 0}
              onChange={(next) => void changeStatus(next)}
            />
            {can.revise && (
              <button type="button" className="btn-secondary" disabled={busy} onClick={() => void revise()}>
                Create revision
              </button>
            )}
            {can.convert && (
              <button type="button" className="btn-primary" disabled={busy} onClick={() => setConvertOpen(true)}>
                Make it a job
              </button>
            )}
            <Link to="/app/quotes" className="btn-secondary">
              Back to quotes
            </Link>
            <ActionMenu
              label="More actions"
              items={[
                { label: archived ? 'Restore' : 'Archive', tone: archived ? undefined : 'warning', onClick: () => void archiveToggle() },
                isAdmin && { label: 'Correct dates', onClick: () => setDatesOpen(true) },
                isAdmin && can.delete && { label: 'Delete', tone: 'danger', onClick: () => void remove() },
              ]}
            />
          </>
        }
      />

      {stale && (
        <Banner kind="error">
          {STALE_MESSAGE}{' '}
          <button type="button" className="row-action" onClick={() => void load()}>
            Reload
          </button>
        </Banner>
      )}
      {error && <Banner kind="error">{error}</Banner>}
      {notice && <Banner kind="ok">{notice}</Banner>}
      {quote.converted && quote.convertedJobId && (
        <p className="banner-info mb-4" role="status">
          This quote became{' '}
          <Link className="table-link" to={`/app/jobs/${quote.convertedJobId}`}>
            {quote.convertedJobNumber}
          </Link>
          . It is kept as it was won; only notes can still be added.
        </p>
      )}

      <div className="mb-4 flex gap-1 overflow-x-auto border-b border-gray-200">
        {QUOTE_TABS.map((section) => {
          const active = tab === section.key;
          return (
            <button
              key={section.key}
              type="button"
              onClick={() => setTab(section.key)}
              aria-current={active ? 'page' : undefined}
              className={`min-h-11 whitespace-nowrap border-b-2 px-3 text-sm ${
                active
                  ? 'border-brand-700 font-medium text-brand-800'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              {section.label}
            </button>
          );
        })}
      </div>

      {tab === 'details' && (
        <DetailsPanel
          detail={detail}
          onSaved={(next) => {
            setDetail(next);
            setNotice('Saved.');
          }}
          onStale={() => setStale(true)}
        />
      )}

      {tab === 'description' && (
        <DescriptionPanel
          detail={detail}
          onSaved={(next) => {
            setDetail(next);
            setNotice('Description saved.');
          }}
          onStale={() => setStale(true)}
        />
      )}

      {tab === 'exclusions' && (
        <ExclusionsPanel
          detail={detail}
          onSaved={(next) => {
            setDetail(next);
            setNotice('Exclusions saved.');
          }}
          onStale={() => setStale(true)}
        />
      )}

      {/* Mounted while hidden, so unsaved prices survive a look at another tab. */}
      <div hidden={tab !== 'pricing'}>
        <PricingPanel
          // Re-seeded only when the saved lines change (a save gives every line a
          // new id) or a revision starts, never by a save on another tab.
          key={`${revision.id}:${detail.lines.map((line) => line.id).join(",")}`}
          detail={detail}
          onSaved={(next) => {
            setDetail(next);
            setNotice('Prices saved.');
          }}
          onStale={() => setStale(true)}
        />
      </div>

      {tab === 'notes' && (
        <NotesPanel
          title="Internal notes"
          notes={detail.notes}
          canWrite
          intro="For your team. Never shown to the client, and your crew cannot see quotes at all."
          onAdd={async (body) => {
            const { note } = await quotesApi.addNote(quote.id, body);
            setDetail((current) => (current ? { ...current, notes: [note, ...current.notes] } : current));
          }}
        />
      )}

      {tab === 'files' && (
        <>
          <p className="hint mb-3">
            Dayboard does not write the quote document. Keep the one you sent here, with any
            drawings or specs it was priced from. Only managers and admins can see these.
          </p>
          <Documents kind="quote" ownerId={quote.id} title="Files" />
        </>
      )}

      {tab === 'history' && <HistoryPanel detail={detail} />}

      {datesOpen && (
        <DatesModal
          detail={detail}
          onClose={() => setDatesOpen(false)}
          onSaved={(next) => {
            setDatesOpen(false);
            setDetail(next);
            setNotice('Dates corrected. The change is recorded in History.');
          }}
        />
      )}

      {convertOpen && (
        <ConvertModal
          detail={detail}
          onClose={() => setConvertOpen(false)}
          onConverted={(jobId) => navigate(`/app/jobs/${jobId}`)}
        />
      )}
    </>
  );
}

// --- Details ----------------------------------------------------------------

function DetailsPanel({
  detail,
  onSaved,
  onStale,
}: {
  detail: QuoteDetail;
  onSaved: (next: QuoteDetail) => void;
  onStale: () => void;
}) {
  const { quote, revision, can } = detail;
  const [editing, setEditing] = useState(false);
  // Pipeline facts (who, by when) stay editable after sending; content does not.
  const canEditPipeline = !quote.archived && !quote.converted;

  return (
    <section className="panel max-w-3xl">
      <div className="row mb-3">
        <h2 className="!mb-0">Details</h2>
        <span className="spacer" />
        {canEditPipeline && (
          <button className="secondary" onClick={() => setEditing((open) => !open)}>
            {editing ? 'Cancel' : 'Edit'}
          </button>
        )}
      </div>

      {editing ? (
        <EditDetailsForm
          detail={detail}
          contentEditable={can.edit}
          onSaved={(next) => {
            setEditing(false);
            onSaved(next);
          }}
          onStale={onStale}
        />
      ) : (
        <dl className="details">
          {/* Every field, every time (round 45, Nathan: "showing a dash if
              empty"), so a missing detail reads as missing rather than as a
              shorter page. Description and Exclusions have their own tabs. */}
          <dt>Quote number</dt>
          <dd>{quote.number}</dd>
          <dt>Title</dt>
          <dd>{revision.title || DASH}</dd>
          <dt>Client</dt>
          <dd>{quote.clientName}</dd>
          <dt>Representative</dt>
          <dd>
            {detail.rep ? (
              <>
                {detail.rep.name}
                {detail.rep.phone && <> · {detail.rep.phone}</>}
                {detail.rep.email && <> · {detail.rep.email}</>}
              </>
            ) : (
              DASH
            )}
          </dd>
          <dt>Quote manager</dt>
          <dd>{quote.quoteManagerName ?? DASH}</dd>
          <dt>Site address</dt>
          <dd>{revision.siteAddress || DASH}</dd>
          <dt>Status</dt>
          <dd>{QUOTE_STATUS_LABEL[quote.status]}</dd>
          <dt>Revision</dt>
          <dd>{revision.revisionNo}</dd>
          <dt>Quote date</dt>
          <dd>{formatDate(quote.quoteDate)}</dd>
          <dt>Submission due</dt>
          <dd>{quote.submissionDueDate ? formatDate(quote.submissionDueDate) : DASH}</dd>
          <dt>Valid until</dt>
          <dd>{revision.validUntil ? formatDate(revision.validUntil) : DASH}</dd>
          <dt>Sent</dt>
          <dd>{revision.sentDate ? formatDate(revision.sentDate) : DASH}</dd>
          <dt>Decided</dt>
          <dd>{quote.decidedDate ? formatDate(quote.decidedDate) : DASH}</dd>
          <dt>{revision.gstApplicable ? 'Total (incl GST)' : 'Total'}</dt>
          <dd className="tabular-nums">{formatCents(revision.totalCents)}</dd>
        </dl>
      )}
      {!can.edit && !quote.converted && !quote.archived && (
        <p className="hint mt-3">
          The details and prices are locked because this quote is marked{' '}
          {QUOTE_STATUS_LABEL[quote.status]}.{' '}
          {can.revise
            ? 'Make a revision to re-price it.'
            : 'Set it back to Draft to change them.'}{' '}
          The quote manager, representative and submission date can still change.
        </p>
      )}
    </section>
  );
}

function EditDetailsForm({
  detail,
  contentEditable,
  onSaved,
  onStale,
}: {
  detail: QuoteDetail;
  contentEditable: boolean;
  onSaved: (next: QuoteDetail) => void;
  onStale: () => void;
}) {
  const { quote, revision } = detail;
  const [clients, setClients] = useState<Client[]>([]);
  const [clientId, setClientId] = useState(quote.clientId);
  const [reps, setReps] = useState<ClientRep[]>([]);
  const [addingRep, setAddingRep] = useState(false);
  const [repId, setRepId] = useState(detail.clientRepId ?? '');
  const [people, setPeople] = useState<{ id: string; name: string }[]>([]);
  const [managerId, setManagerId] = useState(quote.quoteManagerId ?? '');
  const [title, setTitle] = useState(revision.title);
  const [siteAddress, setSiteAddress] = useState(revision.siteAddress ?? '');
  const [validUntil, setValidUntil] = useState(revision.validUntil ?? '');
  const [submissionDueDate, setSubmissionDueDate] = useState(quote.submissionDueDate ?? '');
  const [fields, setFields] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (contentEditable) {
      void scheduling.listClients().then((r) => setClients(r.clients), () => setClients([]));
    }
    void notifications.people().then((r) => setPeople(r.people), () => setPeople([]));
  }, [contentEditable]);

  useEffect(() => {
    void scheduling.listClientReps(clientId).then(
      (r) => setReps(r.reps.filter((rep) => !rep.archived || rep.id === detail.clientRepId)),
      () => setReps([])
    );
  }, [clientId, detail.clientRepId]);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setFields({});
    try {
      const next = await quotesApi.update(quote.id, {
        lockVersion: detail.lockVersion,
        // Content only while it is a draft: a form that cannot change a field
        // must OMIT it rather than send what it shows (the form-omission rule).
        ...(contentEditable
          ? {
              ...(clientId !== quote.clientId ? { clientId } : {}),
              title,
              siteAddress: siteAddress || null,
              validUntil: validUntil || null,
            }
          : {}),
        clientRepId: repId || null,
        submissionDueDate: submissionDueDate || null,
        ...(people.length > 0 ? { quoteManagerId: managerId || null } : {}),
      });
      onSaved(next);
    } catch (err) {
      if (isStale(err)) onStale();
      if (err instanceof ApiError) {
        setError(err.message);
        setFields(err.fields);
      } else {
        setError('Could not save that.');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} noValidate className="space-y-1">
      {error && <Banner kind="error">{error}</Banner>}
      {/* Round 45 (Nathan: "slight bunching up around rep box, quote manager,
          submission due, valid until"). Every row is a .row.pair of two
          same-shaped labels, so the boxes line up down the form; the "Add
          representative" action sits UNDER its pair rather than inside a
          label row the select beside it does not have. Description and
          Exclusions moved to their own tabs. */}
      {contentEditable && (
        <>
          <div className="row pair">
            <label htmlFor="quote-client">
              Client
              <select
                id="quote-client"
                value={clientId}
                onChange={(event) => {
                  setClientId(event.target.value);
                  setRepId('');
                }}
              >
                {clients.length === 0 && <option value={quote.clientId}>{quote.clientName}</option>}
                {clients.map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.name}
                  </option>
                ))}
              </select>
            </label>
            <Field label="Title" name="title" value={title} onChange={setTitle} error={fields.title} />
          </div>
          <div className="row">
            <Field label="Site address" name="siteAddress" value={siteAddress} onChange={setSiteAddress} required={false} />
          </div>
        </>
      )}
      <div className="row pair">
        <label htmlFor="quote-rep">
          Representative
          <select id="quote-rep" value={repId} onChange={(event) => setRepId(event.target.value)}>
            <option value="">Nobody named</option>
            {reps.map((rep) => (
              <option key={rep.id} value={rep.id}>
                {rep.name}
              </option>
            ))}
          </select>
        </label>
        {people.length > 0 ? (
          <label htmlFor="quote-manager">
            Quote manager
            <select id="quote-manager" value={managerId} onChange={(event) => setManagerId(event.target.value)}>
              <option value="">Nobody yet</option>
              {/* A manager who has since left is not on the people list, but
                  is still on the quote: shown by name rather than as "Nobody",
                  which misread the quote and sent its old id unseen. */}
              {quote.quoteManagerId &&
                !people.some((person) => person.id === quote.quoteManagerId) && (
                  <option value={quote.quoteManagerId}>
                    {quote.quoteManagerName ?? 'Previous manager'} (no longer active)
                  </option>
                )}
              {people.map((person) => (
                <option key={person.id} value={person.id}>
                  {person.name}
                </option>
              ))}
            </select>
          </label>
        ) : (
          <span />
        )}
      </div>
      {/* Created, then chosen (ZT's flow), as New client is on a new job. */}
      <p className="!mb-3">
        <button type="button" className="row-action" onClick={() => setAddingRep(true)}>
          Add representative
        </button>
      </p>
      <div className="row pair">
        <Field
          label="Submission due"
          name="submissionDueDate"
          type="date"
          value={submissionDueDate}
          onChange={setSubmissionDueDate}
          error={fields.submissionDueDate}
          required={false}
        />
        {contentEditable ? (
          <Field
            label="Valid until"
            name="validUntil"
            type="date"
            value={validUntil}
            onChange={setValidUntil}
            error={fields.validUntil}
            required={false}
          />
        ) : (
          <span />
        )}
      </div>
      <button type="submit" className="btn-primary mt-4" disabled={busy}>
        {busy ? 'Saving…' : 'Save'}
      </button>
      {addingRep && (
        <RepModal
          clientId={clientId}
          rep={null}
          onClose={() => setAddingRep(false)}
          onSaved={(saved) => {
            setAddingRep(false);
            setReps((current) => [...current, saved]);
            setRepId(saved.id);
          }}
        />
      )}
    </form>
  );
}

// --- Description and Exclusions (round 45: tabs of their own) ---------------

const DASH = <span className="muted">—</span>;

/** Saves one content field, with the quote's lockVersion, and reports back. */
function useContentSave(
  detail: QuoteDetail,
  onSaved: (next: QuoteDetail) => void,
  onStale: () => void
) {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  async function save(patch: { description?: string | null; exclusions?: string | null }) {
    setBusy(true);
    setError(null);
    try {
      onSaved(await quotesApi.update(detail.quote.id, { lockVersion: detail.lockVersion, ...patch }));
      return true;
    } catch (err) {
      if (isStale(err)) onStale();
      setError(err instanceof ApiError ? err.message : 'Could not save that.');
      return false;
    } finally {
      setBusy(false);
    }
  }
  return { save, error, busy };
}

function LockedHint({ detail }: { detail: QuoteDetail }) {
  const { quote, can } = detail;
  if (can.edit || quote.converted || quote.archived) return null;
  return (
    <p className="hint mt-3">
      Locked because this quote is marked {QUOTE_STATUS_LABEL[quote.status]}.{' '}
      {can.revise ? 'Create a revision to change it.' : 'Set it back to Draft to change it.'}
    </p>
  );
}

function DescriptionPanel({
  detail,
  onSaved,
  onStale,
}: {
  detail: QuoteDetail;
  onSaved: (next: QuoteDetail) => void;
  onStale: () => void;
}) {
  const { revision, can } = detail;
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(revision.description ?? '');
  const { save, error, busy } = useContentSave(detail, onSaved, onStale);

  return (
    <section className="panel max-w-3xl">
      <div className="row mb-3">
        <h2 className="!mb-0">Description</h2>
        <span className="spacer" />
        {can.edit && !editing && (
          <button type="button" className="btn-secondary btn-sm" onClick={() => setEditing(true)}>
            Edit
          </button>
        )}
      </div>
      <p className="hint mb-3">What the work is, in the words you would put on the quote you send.</p>
      {error && <Banner kind="error">{error}</Banner>}
      {editing ? (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void save({ description: text.trim() === '' ? null : text }).then((ok) => ok && setEditing(false));
          }}
        >
          <label htmlFor="quote-description">
            <span className="sr-only">Description</span>
            <textarea
              id="quote-description"
              rows={8}
              value={text}
              onChange={(event) => setText(event.target.value)}
            />
          </label>
          <div className="mt-3 flex gap-2">
            <button type="submit" className="btn-primary" disabled={busy}>
              {busy ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => {
                setText(revision.description ?? '');
                setEditing(false);
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      ) : revision.description ? (
        <p className="whitespace-pre-line">{revision.description}</p>
      ) : (
        <p className="muted">No description yet.</p>
      )}
      <LockedHint detail={detail} />
    </section>
  );
}

/** The stored column is newline-joined; the editor is one box per line (ZT's). */
function splitExclusions(value: string | null): string[] {
  return (value ?? '')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
}

function ExclusionsPanel({
  detail,
  onSaved,
  onStale,
}: {
  detail: QuoteDetail;
  onSaved: (next: QuoteDetail) => void;
  onStale: () => void;
}) {
  const { revision, can } = detail;
  const saved = splitExclusions(revision.exclusions);
  const [editing, setEditing] = useState(false);
  const [rows, setRows] = useState<string[]>(saved.length > 0 ? saved : ['']);
  const { save, error, busy } = useContentSave(detail, onSaved, onStale);

  function begin() {
    setRows(saved.length > 0 ? saved : ['']);
    setEditing(true);
  }

  return (
    <section className="panel max-w-3xl">
      <div className="row mb-3">
        <h2 className="!mb-0">Exclusions and assumptions</h2>
        <span className="spacer" />
        {can.edit && !editing && (
          <button type="button" className="btn-secondary btn-sm" onClick={begin}>
            Edit
          </button>
        )}
      </div>
      <p className="hint mb-3">What the price does not cover, and what it assumes. One per line.</p>
      {error && <Banner kind="error">{error}</Banner>}
      {editing ? (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            const kept = rows.map((row) => row.trim()).filter(Boolean);
            void save({ exclusions: kept.length > 0 ? kept.join('\n') : null }).then(
              (ok) => ok && setEditing(false)
            );
          }}
        >
          <ul className="m-0 list-none space-y-2 p-0">
            {rows.map((row, index) => (
              <li key={index} className="flex items-center gap-2">
                <span aria-hidden="true" className="w-3 flex-none text-center text-brand-800">
                  •
                </span>
                <input
                  className="form-input"
                  aria-label={`Exclusion ${index + 1}`}
                  value={row}
                  onChange={(event) =>
                    setRows((current) => current.map((r, i) => (i === index ? event.target.value : r)))
                  }
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      setRows((current) => [...current.slice(0, index + 1), '', ...current.slice(index + 1)]);
                    }
                  }}
                />
                <button
                  type="button"
                  className="row-action-danger flex-none"
                  aria-label={`Remove exclusion ${index + 1}`}
                  onClick={() =>
                    setRows((current) => (current.length === 1 ? [''] : current.filter((_, i) => i !== index)))
                  }
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
          <button
            type="button"
            className="row-action mt-2"
            onClick={() => setRows((current) => [...current, ''])}
          >
            + Add exclusion or assumption
          </button>
          <div className="mt-4 flex gap-2">
            <button type="submit" className="btn-primary" disabled={busy}>
              {busy ? 'Saving…' : 'Save'}
            </button>
            <button type="button" className="btn-secondary" onClick={() => setEditing(false)}>
              Cancel
            </button>
          </div>
        </form>
      ) : saved.length > 0 ? (
        <ul className="list-disc pl-5">
          {saved.map((line, index) => (
            <li key={index}>{line}</li>
          ))}
        </ul>
      ) : (
        <p className="muted">None listed.</p>
      )}
      <LockedHint detail={detail} />
    </section>
  );
}

/** ZT's revision pill: "Rev 2", indigo, on the list and in the header. */
export function RevisionPill({ revision, round = false }: { revision: number; round?: boolean }) {
  return (
    <span
      className={`whitespace-nowrap bg-indigo-100 font-medium text-indigo-700 ${
        round ? 'rounded-full px-2 py-0.5 text-xs' : 'rounded px-1.5 py-0.5 text-[10px]'
      }`}
    >
      Rev {revision}
    </span>
  );
}

// --- Pricing ----------------------------------------------------------------

interface DraftLine extends LineInput {
  key: string;
}

let lineKey = 0;
const nextKey = () => `line-${(lineKey += 1)}`;

function toDraft(line: QuoteLine): DraftLine {
  return {
    key: nextKey(),
    description: line.description,
    unit: line.unit ?? '',
    quantity: line.quantity,
    rate: centsToInput(line.rateCents),
  };
}

const EMPTY_LINE = (): DraftLine => ({ key: nextKey(), description: '', unit: '', quantity: '1', rate: '' });

/**
 * A preview of one line's total while it is being typed. ONLY a preview: the
 * server prices every line on save (quotes/money.ts) and the saved figures
 * replace these. It rounds the way the server does so the two agree, but if
 * they ever differed, the server's number is the one that is stored.
 */
function previewCents(quantity: string, rate: string): number | null {
  const units = (value: string, places: number) => {
    if (!/^\s*-?(\d+\.?\d*|\.\d+)\s*$/.test(value)) return null;
    const n = Number(value);
    const abs = Math.abs(n);
    const s = String(abs);
    const scaled = s.includes('e') ? abs * 10 ** places : Number(`${s}e${places}`);
    return (n < 0 ? -1 : 1) * Math.round(scaled);
  };
  const q = units(quantity, 4);
  const r = units(rate, 2);
  if (q === null || r === null) return null;
  const product = q * r;
  return Math.sign(product) * Math.floor((Math.abs(product) + 5000) / 10000);
}

function PricingPanel({
  detail,
  onSaved,
  onStale,
}: {
  detail: QuoteDetail;
  onSaved: (next: QuoteDetail) => void;
  onStale: () => void;
}) {
  const { quote, revision, lines, can } = detail;
  const [draft, setDraft] = useState<DraftLine[]>(() =>
    lines.length > 0 ? lines.map(toDraft) : can.edit ? [EMPTY_LINE()] : []
  );
  const [error, setError] = useState<string | null>(null);
  const [badLine, setBadLine] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);

  const saved = useMemo(() => JSON.stringify(lines.map(toComparable)), [lines]);
  const dirty =
    can.edit &&
    JSON.stringify(draft.filter((l) => !isBlank(l)).map(toComparable)) !== saved;

  // Leaving the page with unsaved prices asks first (the form builder's rule).
  useEffect(() => {
    if (!dirty) return;
    const warn = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  function update(index: number, patch: Partial<DraftLine>) {
    setDraft((current) => current.map((line, i) => (i === index ? { ...line, ...patch } : line)));
  }

  const [focusKey, setFocusKey] = useState<string | null>(null);

  useEffect(() => {
    if (!focusKey) return;
    document.getElementById(`line-${focusKey}-description`)?.focus();
    setFocusKey(null);
  }, [focusKey]);

  function addLine() {
    const line = EMPTY_LINE();
    setDraft((current) => [...current, line]);
    setFocusKey(line.key);
  }

  function remove(index: number) {
    setDraft((current) => current.filter((_, i) => i !== index));
  }

  function move(index: number, by: -1 | 1) {
    setDraft((current) => {
      const next = [...current];
      const target = index + by;
      if (target < 0 || target >= next.length) return current;
      [next[index], next[target]] = [next[target]!, next[index]!];
      return next;
    });
  }

  async function save() {
    setBusy(true);
    setError(null);
    setBadLine(null);
    // Blank lines are dropped from the PAGE as well as the request, so the
    // server's "Line 3" is the third line on screen and the right one turns
    // red (review, 2026-10-04: with a blank line above, they disagreed).
    const kept = draft.filter((line) => !isBlank(line));
    setDraft(kept);
    try {
      const next = await quotesApi.saveLines(
        quote.id,
        detail.lockVersion,
        kept.map(({ description, unit, quantity, rate }) => ({
          description,
          unit,
          quantity,
          rate,
        }))
      );
      onSaved(next);
    } catch (err) {
      if (isStale(err)) onStale();
      if (err instanceof ApiError) {
        setError(err.message);
        const line = (err.details as { line?: number } | undefined)?.line;
        if (typeof line === 'number') setBadLine(line);
      } else {
        setError('Could not save the prices.');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="panel">
      <div className="row mb-3">
        <h2 className="!mb-0">
          Pricing{quote.currentRevision > 1 && <span className="muted"> · revision {revision.revisionNo}</span>}
        </h2>
        <span className="spacer" />
        {can.edit && (
          <button type="button" className="btn-primary" disabled={busy || !dirty} onClick={() => void save()}>
            {busy ? 'Saving…' : 'Save prices'}
          </button>
        )}
      </div>
      {error && <Banner kind="error">{error}</Banner>}
      <p className="hint mb-3">
        Rates are entered excluding GST.
        {revision.gstApplicable
          ? ' GST is added to the subtotal once, at 10%.'
          : ' Your company is set as not registered for GST, so no GST is added.'}
        {!can.edit && ' These prices are locked; see Details for why.'}
      </p>

      {can.edit ? (
        <>
          {/* Round 45 (Nathan: "Line X | Description | Quantity | Unit | Rate
              | Amount ... like a nice excel spreadsheet row"). ONE set of
              inputs: a row of cells from sm up, the same cells stacked with
              their labels on a phone, so the two can never disagree. */}
          <div className="overflow-hidden rounded-lg border border-gray-300" role="table" aria-label="Quote lines">
            <div
              role="row"
              className={`hidden border-b border-gray-300 bg-gray-50 text-xs font-semibold uppercase tracking-wide text-gray-600 sm:grid ${LINE_GRID}`}
            >
              <div role="columnheader" className="px-3 py-2">Line</div>
              <div role="columnheader" className="border-l border-gray-200 px-3 py-2">Description</div>
              <div role="columnheader" className="border-l border-gray-200 px-3 py-2 text-center">Quantity</div>
              <div role="columnheader" className="border-l border-gray-200 px-3 py-2 text-center">Unit</div>
              <div role="columnheader" className="border-l border-gray-200 px-3 py-2 text-center">Rate</div>
              <div role="columnheader" className="border-l border-gray-200 px-3 py-2 text-center">Amount</div>
              <div role="columnheader" className="border-l border-gray-200 px-1 py-2">
                <span className="sr-only">Actions</span>
              </div>
            </div>
            {draft.map((line, index) => {
              const preview = previewCents(line.quantity, line.rate);
              const bad = badLine === index;
              return (
                <div
                  key={line.key}
                  role="row"
                  data-line={index + 1}
                  className={`grid grid-cols-2 gap-x-3 gap-y-2 border-b p-3 last:border-b-0 sm:gap-0 sm:p-0 ${LINE_GRID} ${
                    bad ? 'border-red-400 bg-red-50' : 'border-gray-200 even:bg-gray-50/40'
                  }`}
                >
                  <div role="cell" className="col-span-1 self-center text-sm font-medium text-gray-700 sm:px-3 sm:py-2">
                    Line {index + 1}
                  </div>
                  <div role="cell" className="col-span-1 flex justify-end sm:hidden">
                    <LineMenu index={index} count={draft.length} onMove={move} onRemove={() => remove(index)} />
                  </div>
                  <label role="cell" className={`col-span-2 sm:col-span-1 ${CELL}`}>
                    <span className={CELL_LABEL}>Description</span>
                    <input
                      id={`line-${line.key}-description`}
                      className={CELL_INPUT}
                      value={line.description}
                      onChange={(event) => update(index, { description: event.target.value })}
                    />
                  </label>
                  <label role="cell" className={CELL}>
                    <span className={CELL_LABEL}>Quantity</span>
                    <input
                      className={`${CELL_INPUT} tabular-nums sm:text-center`}
                      inputMode="decimal"
                      value={line.quantity}
                      onChange={(event) => update(index, { quantity: event.target.value })}
                    />
                  </label>
                  <label role="cell" className={CELL}>
                    <span className={CELL_LABEL}>Unit</span>
                    <input
                      className={`${CELL_INPUT} sm:text-center`}
                      placeholder="m², lm, hr, ea"
                      value={line.unit}
                      onChange={(event) => update(index, { unit: event.target.value })}
                    />
                  </label>
                  <label role="cell" className={CELL}>
                    <span className={CELL_LABEL}>Rate ($, excl GST)</span>
                    <RateInput
                      value={line.rate}
                      onChange={(rate) => update(index, { rate })}
                      onKeyDown={(event) => {
                        // Enter on the last line's rate starts the next line,
                        // the way a spreadsheet moves down a row.
                        if (event.key === 'Enter' && index === draft.length - 1) {
                          event.preventDefault();
                          addLine();
                        }
                      }}
                    />
                  </label>
                  <div
                    role="cell"
                    className={`self-center text-right text-sm tabular-nums sm:flex sm:items-center sm:justify-center sm:border-l sm:border-gray-200 sm:px-3 sm:py-2 ${
                      preview === null ? 'text-gray-500' : 'font-medium text-gray-900'
                    }`}
                  >
                    <span className="sm:sr-only text-xs font-normal text-gray-600">Amount </span>
                    {preview === null ? '—' : formatCents(preview)}
                  </div>
                  <div role="cell" className="hidden items-center justify-center sm:flex sm:border-l sm:border-gray-200">
                    <LineMenu index={index} count={draft.length} onMove={move} onRemove={() => remove(index)} />
                  </div>
                </div>
              );
            })}
            {/* Add line stands out and sits where the next line will appear. */}
            <button
              type="button"
              onClick={addLine}
              className="flex min-h-11 w-full items-center justify-center gap-2 border-t border-dashed border-brand-200 bg-brand-50/70 px-3 py-2.5 text-sm font-semibold text-brand-800 transition-colors hover:bg-brand-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-600"
            >
              <span aria-hidden="true" className="text-base leading-none">+</span>
              Add line
            </button>
          </div>
          <p className="hint mt-2">
            A negative rate makes a credit line. Quantities cannot be negative. Enter on the last
            rate starts a new line.
          </p>
        </>
      ) : (
        <LinesTable lines={lines} />
      )}

      <Totals revision={revision} pending={dirty} />
    </section>
  );
}

/** Line | Description | Quantity | Unit | Rate | Amount | menu, from sm up. */
const LINE_GRID =
  'sm:grid-cols-[4.5rem_minmax(0,1fr)_6.5rem_6rem_8rem_8.5rem_3rem] sm:items-stretch';
const CELL = 'block min-w-0 sm:border-l sm:border-gray-200';
const CELL_LABEL = 'form-label sm:sr-only';
/** Borderless inside a cell from sm up, so the row reads as a spreadsheet. */
const CELL_INPUT =
  'form-input sm:h-full sm:rounded-none sm:border-0 sm:bg-transparent sm:shadow-none sm:focus:bg-white sm:focus:ring-2 sm:focus:ring-inset sm:focus:ring-brand-600';

/**
 * The rate box reads like the Amount beside it ("$1,250.50", Nathan,
 * 2026-10-14) while nobody is typing in it, and shows the plain number while
 * focused so editing never fights the formatting. A "$" or "," typed or
 * pasted is dropped as it arrives, so the draft (and the save) only ever hold
 * the plain number the server's strict parser takes (quotes/money.ts).
 */
function RateInput({
  value,
  onChange,
  onKeyDown,
}: {
  value: string;
  onChange: (rate: string) => void;
  onKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void;
}) {
  const [focused, setFocused] = useState(false);
  const cents = previewCents('1', value);
  return (
    <input
      className={`${CELL_INPUT} tabular-nums sm:text-center`}
      inputMode="decimal"
      value={focused || cents === null ? value : formatCents(cents)}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      onChange={(event) => onChange(event.target.value.replace(/[$,\s]/g, ''))}
      onKeyDown={onKeyDown}
    />
  );
}

function LineMenu({
  index,
  count,
  onMove,
  onRemove,
}: {
  index: number;
  count: number;
  onMove: (index: number, by: -1 | 1) => void;
  onRemove: () => void;
}) {
  return (
    <ActionMenu
      label={`Line ${index + 1} actions`}
      items={[
        { label: 'Move up', onClick: () => onMove(index, -1), disabled: index === 0 },
        { label: 'Move down', onClick: () => onMove(index, 1), disabled: index === count - 1 },
        { label: 'Remove line', tone: 'danger', onClick: onRemove },
      ]}
    />
  );
}

function isBlank(line: DraftLine): boolean {
  return line.description.trim() === '' && line.rate.trim() === '' && (line.quantity.trim() === '' || line.quantity.trim() === '1');
}

function toComparable(line: { description: string; unit: string | null; quantity: string; rate?: string; rateCents?: number }) {
  return [
    line.description.trim(),
    (line.unit ?? '').trim(),
    Number(line.quantity),
    line.rate !== undefined ? previewCents('1', line.rate) : line.rateCents,
  ];
}

function LinesTable({ lines }: { lines: QuoteLine[] }) {
  if (lines.length === 0) return <p className="muted">No lines on this revision.</p>;
  return (
    <div className="table-wrap">
      <table className="data-table">
        <thead>
          <tr>
            <th>#</th>
            <th>Description</th>
            <th className="text-center">Qty</th>
            <th className="text-center">Unit</th>
            <th className="text-center">Rate</th>
            <th className="text-center">Amount</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((line, index) => (
            <tr key={line.id}>
              <td className="table-muted tabular-nums">{index + 1}</td>
              <td className="whitespace-pre-line">{line.description}</td>
              <td className="text-center tabular-nums">{line.quantity}</td>
              <td className="table-muted text-center">{line.unit}</td>
              <td className="whitespace-nowrap text-center tabular-nums">{formatCents(line.rateCents)}</td>
              <td className="whitespace-nowrap text-center tabular-nums">{formatCents(line.lineTotalCents)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Subtotal, GST and total, as saved. With GST off there is NO GST line at all,
 * never one reading $0.00 (the tax-document rule, applied here too).
 */
function Totals({ revision, pending }: { revision: QuoteRevision; pending: boolean }) {
  return (
    <dl className="ml-auto mt-4 grid max-w-xs grid-cols-2 gap-x-4 gap-y-1 text-sm">
      <dt className="text-gray-600">Subtotal (excl GST)</dt>
      <dd className="text-right tabular-nums">{formatCents(revision.subtotalCents)}</dd>
      {revision.gstApplicable && (
        <>
          <dt className="text-gray-600">GST</dt>
          <dd className="text-right tabular-nums">{formatCents(revision.gstCents)}</dd>
        </>
      )}
      <dt className="font-semibold">{revision.gstApplicable ? 'Total (incl GST)' : 'Total'}</dt>
      <dd className="text-right font-semibold tabular-nums">{formatCents(revision.totalCents)}</dd>
      {pending && <dd className="col-span-2 text-right text-xs text-amber-800">Save to update the totals.</dd>}
    </dl>
  );
}

// --- History ----------------------------------------------------------------

const EVENT_TEXT: Record<string, (e: QuoteDetail['events'][number]) => string> = {
  created: () => 'Created',
  status_changed: (e) =>
    `${e.fromStatus ? QUOTE_STATUS_LABEL[e.fromStatus] : ''} to ${e.toStatus ? QUOTE_STATUS_LABEL[e.toStatus] : ''}`,
  revised: (e) => `Revision ${e.revisionNo} started`,
  converted: () => 'Made into a job',
  dates_corrected: () => 'Dates corrected',
  archived: () => 'Archived',
  unarchived: () => 'Restored',
  // Round 45.
  details_changed: () => 'Details changed',
  prices_changed: (e) => {
    const total = e.detail.totalCents as { from: number; to: number } | undefined;
    return total && total.from !== total.to
      ? `Prices changed: ${formatCents(total.from)} to ${formatCents(total.to)}`
      : 'Prices changed';
  },
  note_added: () => 'Note added',
  file_added: (e) => `File added: ${String(e.detail.filename ?? '')}`,
  file_removed: (e) => `File removed: ${String(e.detail.filename ?? '')}`,
};

const CHANGE_LABEL: Record<string, string> = {
  client: 'Client',
  title: 'Title',
  siteAddress: 'Site address',
  rep: 'Representative',
  manager: 'Quote manager',
  submissionDue: 'Submission due',
  validUntil: 'Valid until',
  description: 'Description',
  exclusions: 'Exclusions',
};
const DATE_CHANGES = new Set(['submissionDue', 'validUntil']);

/** One details_changed entry, a line per field (src/quotes/events.ts's shape). */
function DetailsChanges({ detail, names }: { detail: Record<string, unknown>; names: Record<string, string> }) {
  const changes = (detail.changes ?? []) as { field: string; from?: string | null; to?: string | null }[];
  const show = (field: string, value: string | null | undefined) => {
    if (!value) return 'none';
    if (DATE_CHANGES.has(field)) return formatDate(value);
    return names[value] ?? 'someone since removed';
  };
  return (
    <ul className="muted mt-1 list-disc pl-5">
      {changes.map((change, index) => (
        <li key={index}>
          {CHANGE_LABEL[change.field] ?? change.field}
          {'from' in change || 'to' in change
            ? `: ${show(change.field, change.from)} to ${show(change.field, change.to)}`
            : ' changed'}
        </li>
      ))}
    </ul>
  );
}

function HistoryPanel({ detail }: { detail: QuoteDetail }) {
  const [viewing, setViewing] = useState<{ revision: QuoteRevision; lines: QuoteLine[] } | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function open(no: number) {
    try {
      setViewing(await quotesApi.revision(detail.quote.id, no));
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not open that revision.');
    }
  }

  return (
    <>
      <section className="panel">
        <h2>Revisions</h2>
        {error && <Banner kind="error">{error}</Banner>}
        <ul className="divide-y divide-gray-100">
          {detail.revisions.map((revision) => (
            <li key={revision.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2 text-sm">
              <span className="font-medium">Revision {revision.revisionNo}</span>
              <span className="tabular-nums">{formatCents(revision.totalCents)}</span>
              <span className="muted">
                {revision.sentDate ? `sent ${formatDate(revision.sentDate)}` : 'not sent'} · by {revision.createdByName}
              </span>
              <span className="spacer flex-1" />
              {revision.revisionNo !== detail.quote.currentRevision && (
                <button type="button" className="row-action" onClick={() => void open(revision.revisionNo)}>
                  View
                </button>
              )}
            </li>
          ))}
        </ul>
      </section>

      <section className="panel">
        <h2>What happened</h2>
        <ul className="divide-y divide-gray-100">
          {detail.events.map((event) => (
            <li key={event.id} className="py-2 text-sm">
              <span className="font-medium">{EVENT_TEXT[event.kind]?.(event) ?? event.kind}</span>
              <span className="muted">
                {' '}
                · {event.actorName} ·{' '}
                {formatMomentAu(event.createdAt)}
              </span>
              {event.kind === 'dates_corrected' && <DatesChange detail={event.detail} />}
              {event.kind === 'details_changed' && (
                <DetailsChanges detail={event.detail} names={detail.eventNames ?? {}} />
              )}
            </li>
          ))}
        </ul>
      </section>

      {viewing && (
        <Modal title={`Revision ${viewing.revision.revisionNo}`} onClose={() => setViewing(null)} size="xl">
          <p className="muted mb-3">
            {viewing.revision.title}
            {viewing.revision.sentDate && <> · sent {formatDate(viewing.revision.sentDate)}</>}
          </p>
          <LinesTable lines={viewing.lines} />
          <Totals revision={viewing.revision} pending={false} />
        </Modal>
      )}
    </>
  );
}

function DatesChange({ detail }: { detail: Record<string, unknown> }) {
  const before = (detail.before ?? {}) as Record<string, string | null>;
  const after = (detail.after ?? {}) as Record<string, string | null>;
  const names: Record<string, string> = { quoteDate: 'Quote date', sentDate: 'Sent', decidedDate: 'Decided' };
  const changed = Object.keys(names).filter((key) => before[key] !== after[key]);
  return (
    <ul className="muted mt-1 list-disc pl-5">
      {changed.map((key) => (
        <li key={key}>
          {names[key]}: {before[key] ? formatDate(before[key]!) : 'none'} to {after[key] ? formatDate(after[key]!) : 'none'}
        </li>
      ))}
    </ul>
  );
}

// --- Admin date corrections ---------------------------------------------------

function DatesModal({
  detail,
  onClose,
  onSaved,
}: {
  detail: QuoteDetail;
  onClose: () => void;
  onSaved: (next: QuoteDetail) => void;
}) {
  const decided = ['won', 'lost', 'expired'].includes(detail.quote.status);
  const [quoteDate, setQuoteDate] = useState(detail.quote.quoteDate);
  const [sentDate, setSentDate] = useState(detail.revision.sentDate ?? '');
  const [decidedDate, setDecidedDate] = useState(detail.quote.decidedDate ?? '');
  const [fields, setFields] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function save() {
    setBusy(true);
    setError(null);
    setFields({});
    try {
      onSaved(
        await quotesApi.correctDates(detail.quote.id, {
          quoteDate,
          sentDate: sentDate || null,
          ...(decided ? { decidedDate: decidedDate || null } : {}),
        })
      );
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
        setFields(err.fields);
      } else {
        setError('Could not save the dates.');
      }
      setBusy(false);
    }
  }

  return (
    <Modal
      title="Correct dates"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn-primary" disabled={busy} onClick={() => void save()}>
            {busy ? 'Saving…' : 'Save dates'}
          </button>
        </>
      }
    >
      {error && <Banner kind="error">{error}</Banner>}
      <p className="hint mb-3">
        For quotes entered after the fact. The old and new dates are recorded in History.
      </p>
      <Field label="Quote date" name="quoteDate" type="date" value={quoteDate} onChange={setQuoteDate} error={fields.quoteDate} />
      <Field label="Sent" name="sentDate" type="date" value={sentDate} onChange={setSentDate} error={fields.sentDate} required={false} />
      {decided && (
        <Field label="Decided" name="decidedDate" type="date" value={decidedDate} onChange={setDecidedDate} error={fields.decidedDate} required={false} />
      )}
    </Modal>
  );
}

// --- Converting to a job ----------------------------------------------------

/**
 * The crew can open every document on a job they are booked on, so a priced
 * quote copied across would land on their phones. Nothing is ticked by default
 * (round 43); the person converting chooses what the crew should have.
 */
function ConvertModal({
  detail,
  onClose,
  onConverted,
}: {
  detail: QuoteDetail;
  onClose: () => void;
  onConverted: (jobId: string) => void;
}) {
  const [files, setFiles] = useState<Attachment[] | null>(null);
  const [chosen, setChosen] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void documents.list('quote', detail.quote.id).then(
      (r) => setFiles(r.attachments),
      () => setFiles([])
    );
  }, [detail.quote.id]);

  async function convert() {
    setBusy(true);
    setError(null);
    try {
      const { jobId } = await quotesApi.convert(detail.quote.id, [...chosen]);
      onConverted(jobId);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not make the job.');
      setBusy(false);
    }
  }

  return (
    <Modal
      title={`Make ${detail.quote.number} a job`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn-primary" disabled={busy || files === null} onClick={() => void convert()}>
            {busy ? 'Making the job…' : 'Make the job'}
          </button>
        </>
      }
    >
      {error && <Banner kind="error">{error}</Banner>}
      <p className="mb-3">
        The job takes this quote&rsquo;s client, title, site address, representative and quote
        manager, and a copy of its internal notes. The quote&rsquo;s total becomes the job&rsquo;s
        original value.
      </p>
      {files === null ? (
        <p className="muted">Loading the quote&rsquo;s files…</p>
      ) : files.length === 0 ? (
        <p className="muted">This quote has no files to copy.</p>
      ) : (
        <fieldset>
          <legend className="form-label">Copy files to the job</legend>
          <p className="hint mb-2">
            Your crew can open anything on a job they are booked on. Leave the priced quote
            behind; tick drawings and plans they need on site.
          </p>
          {files.map((file) => (
            <label key={file.id} className="flex min-h-11 items-center gap-3">
              <input
                type="checkbox"
                checked={chosen.has(file.id)}
                onChange={(event) =>
                  setChosen((current) => {
                    const next = new Set(current);
                    if (event.target.checked) next.add(file.id);
                    else next.delete(file.id);
                    return next;
                  })
                }
              />
              <span>{file.title}</span>
            </label>
          ))}
        </fieldset>
      )}
    </Modal>
  );
}
